import type { ReactNode } from "react";
import { useMemo } from "react";

import type { ComponentFlowRow, ComponentGroup } from "@busright/data-contract";
import { COLORS, DynamicChart, type DynamicChartRow } from "@busright/ui";

// R3 — purchases by month × component. Pivots the flat (month, component, orderPurpose) rows from
// `useComponentFlow` into one row per month with a column per product group, so `DynamicChart`'s
// switchable metric picker lets an operator flip between tablet / cradle / harness / SIM (or "all
// components") without a bespoke chart per group. Tablet counts are real (Esper-adjacent order
// flow); cradle/harness/SIM are description-parsed estimates — the confidence lives in the caption,
// never folded into the number itself.

const COMPONENT_LABEL: Record<ComponentGroup, string> = {
  tablet: "Tablet",
  cradle: "Cradle / mount",
  harness: "Charging harness",
  sim: "SIM",
};

const COMPONENT_ORDER: readonly ComponentGroup[] = ["tablet", "cradle", "harness", "sim"];

export interface ScComponentPurchasesProps {
  readonly rows: readonly ComponentFlowRow[];
  readonly height?: number;
}

export const ScComponentPurchases = ({
  rows,
  height = 260,
}: ScComponentPurchasesProps): ReactNode => {
  const chartRows = useMemo<DynamicChartRow[]>(() => {
    const byMonth = new Map<string, Record<ComponentGroup, number>>();
    for (const r of rows) {
      const existing = byMonth.get(r.month) ?? { tablet: 0, cradle: 0, harness: 0, sim: 0 };
      existing[r.component] += r.componentQty;
      byMonth.set(r.month, existing);
    }
    return [...byMonth.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, totals]) => ({ month, ...totals }));
  }, [rows]);

  return (
    <DynamicChart
      rows={chartRows}
      dimensions={[{ key: "month", label: "Month" }]}
      metrics={COMPONENT_ORDER.map((c) => ({
        key: c,
        label: COMPONENT_LABEL[c],
        unit: "units",
      }))}
      color={COLORS.accent}
      height={height}
    />
  );
};
